import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PencilIcon, ServerIcon, UserGroupIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMcpConnection, useUpdateMcpConnection, useDeleteMcpConnection } from '../hooks/useMcpConnections';
import { useAgents } from '../hooks/useAgents';
import { McpConnectionForm } from '../components/mcpConnections/McpConnectionForm';
import McpConnectionToolList from '../components/mcpConnections/McpConnectionToolList';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';

export default function McpConnectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: connection, isLoading, error } = useMcpConnection(id);
  const updateMcpConnection = useUpdateMcpConnection();
  const deleteMcpConnection = useDeleteMcpConnection();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  // Fetch all agents to find which ones use this MCP connection
  const { data: agentsData, isLoading: agentsLoading } = useAgents(1, 100);
  const allAgents = agentsData?.data || [];

  // Filter to agents that have this MCP connection linked
  const relatedAgents = allAgents.filter((agent) =>
    agent.mcpConnections?.some((mc) => mc.mcpConnectionId === id)
  );

  const handleSubmit = async (data: any) => {
    await updateMcpConnection.mutateAsync({ id: id!, data });
    setIsEditing(false);
  };

  const handleStartEditName = () => {
    setEditedName(connection?.name || '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === connection?.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await updateMcpConnection.mutateAsync({ id: id!, data: { name: editedName.trim() } });
      setIsEditingName(false);
    } catch (error) {
      console.error('Failed to update name:', error);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading MCP connection...</div>
      </div>
    );
  }

  if (error || !connection) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading MCP connection. Please try again.</div>
      </div>
    );
  }

  const getAuthTypeBadgeClasses = (authType: string) => {
    switch (authType) {
      case 'bearer':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'api-key':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'basic':
        return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getAuthTypeLabel = (authType: string) => {
    switch (authType) {
      case 'bearer':
        return 'Bearer Token';
      case 'api-key':
        return 'API Key';
      case 'basic':
        return 'Basic Auth';
      default:
        return 'None';
    }
  };

  return (
    <div>
      {/* Custom Header with Inline Name Edit */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                autoFocus
                className="text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-indigo-500 focus:outline-none focus:border-indigo-600 px-1"
              />
              <button
                type="button"
                onClick={handleSaveName}
                disabled={updateMcpConnection.isPending}
                className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                title="Save"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleCancelEditName}
                disabled={updateMcpConnection.isPending}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Cancel"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {connection.name}
              </h1>
              {!isEditing && (
                <button
                  type="button"
                  onClick={handleStartEditName}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Edit name"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            MCP Connection #{connection.id} · Created {new Date(connection.createdAt).toLocaleDateString()} · Updated {new Date(connection.updatedAt).toLocaleDateString()}
          </p>
        </div>
        {!isEditing && (
          <div className="mt-4 flex md:ml-4 md:mt-0 space-x-2">
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
              Edit
            </button>
            {/* The one delete affordance (task 864). The two-step header
                swap this replaces turned Delete into Confirm Delete in
                place — a second click on the same spot, which is the
                easiest confirmation in the world to give by accident. */}
            <DeleteResourceControl
              flag="innovate.fe.mcp-connections.delete"
              entity="mcp-connections"
              entityLabel="MCP connection"
              name={connection.name}
              id={connection.id}
              variant="button"
              body={<p>Agents attached to it stop reaching its tools.</p>}
              onDelete={() => deleteMcpConnection.mutateAsync(connection.id)}
              onDeleted={() => navigate('/mcp-connections')}
            />
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <McpConnectionForm
            mode="edit"
            initialData={connection}
            onSubmit={handleSubmit}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Connection Details Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <ServerIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Connection Details</h2>
              </div>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">URL</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono break-all">
                    {connection.url}
                  </dd>
                </div>
                {connection.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {connection.description}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        connection.isActive
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {connection.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Authentication</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getAuthTypeBadgeClasses(
                        connection.authType
                      )}`}
                    >
                      {getAuthTypeLabel(connection.authType)}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Tools Section */}
            <McpConnectionToolList connectionId={connection.id} />
          </div>

          {/* Sidebar - Related Agents */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <UserGroupIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Related Agents</h2>
              </div>

              {agentsLoading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading agents...</div>
              ) : relatedAgents.length === 0 ? (
                <div className="text-center py-6">
                  <UserGroupIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    No agents using this connection
                  </p>
                  <Link
                    to="/agents"
                    className="mt-3 inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
                  >
                    View all agents
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {relatedAgents.map((agent) => (
                    <li key={agent.id} className="py-3 first:pt-0 last:pb-0">
                      <Link
                        to={`/agents/${agent.id}`}
                        className="block hover:bg-gray-50 dark:hover:bg-gray-700 -mx-2 px-2 py-1 rounded"
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {agent.name}
                            </p>
                            {agent.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                {agent.description.length > 50
                                  ? agent.description.substring(0, 50) + '...'
                                  : agent.description}
                              </p>
                            )}
                          </div>
                          <span
                            className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              agent.isActive
                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            {agent.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {relatedAgents.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {relatedAgents.length} {relatedAgents.length === 1 ? 'agent uses' : 'agents use'} this connection
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
